import { useEffect } from 'react';

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  business_name: string | null;
  email: string;
  phone_number: string | null;
  industry: string | null;
  company_size: string | null;
  client_volume: string | null;
  enquiry_frequency: string | null;
  interests: string | null;
  newsletter_opt_in: boolean;
  created_at: string;
}

interface Props {
  member: Member | null;
  onClose: () => void;
}

const industryColors: Record<string, string> = {
  'Real Estate': 'bg-orange-500/15 text-orange-400',
  'Healthcare': 'bg-green-500/15 text-green-400',
  'Finance': 'bg-yellow-500/15 text-yellow-400',
  'Retail': 'bg-pink-500/15 text-pink-400',
  'Technology': 'bg-blue-500/15 text-blue-400',
  'Marketing': 'bg-purple-500/15 text-purple-400',
  'Legal': 'bg-red-500/15 text-red-400',
  'Education': 'bg-cyan-500/15 text-cyan-400',
};

const MemberDetailPanel = ({ member, onClose }: Props) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!member) return null;

  const initials = `${member.first_name[0] || ''}${member.last_name[0] || ''}`.toUpperCase();
  const joinDate = new Date(member.created_at).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const industryColor = member.industry ? (industryColors[member.industry] || 'bg-ea-blue/15 text-ea-blue') : 'bg-ea-dark-card text-ea-gray';

  const interestList = member.interests
    ? member.interests.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-ea-navy border-l border-ea-dark-border flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ea-dark-border shrink-0">
          <h2 className="text-white font-semibold text-sm">Member Profile</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-ea-gray hover:text-white hover:bg-ea-dark-card transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Avatar + name block */}
          <div className="px-6 py-6 border-b border-ea-dark-border">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center shrink-0">
                <span className="text-white text-lg font-bold">{initials}</span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-white font-bold text-base">
                  {member.first_name} {member.last_name}
                </h3>
                {member.business_name && (
                  <p className="text-ea-gray text-sm mt-0.5">{member.business_name}</p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {member.industry && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${industryColor}`}>
                      {member.industry}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${member.newsletter_opt_in ? 'bg-green-500/15 text-green-400' : 'bg-ea-dark-card text-ea-gray/60'}`}>
                    <i className={`${member.newsletter_opt_in ? 'ri-mail-check-line' : 'ri-mail-close-line'} mr-1`}></i>
                    {member.newsletter_opt_in ? 'Newsletter' : 'No Newsletter'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact info */}
          <div className="px-6 py-5 border-b border-ea-dark-border space-y-3">
            <p className="text-ea-gray/50 text-xs font-semibold uppercase tracking-wider">Contact</p>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-ea-blue text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider">Email</p>
                  <p className="text-white text-sm truncate">{member.email}</p>
                </div>
              </div>
              {member.phone_number && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-ea-blue text-sm"></i>
                  </div>
                  <div>
                    <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider">Phone</p>
                    <p className="text-white text-sm">{member.phone_number}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Business details */}
          <div className="px-6 py-5 border-b border-ea-dark-border space-y-3">
            <p className="text-ea-gray/50 text-xs font-semibold uppercase tracking-wider">Business</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-ea-dark-card rounded-xl p-3">
                <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider mb-1">Industry</p>
                <p className="text-white text-sm font-medium">{member.industry || '—'}</p>
              </div>
              <div className="bg-ea-dark-card rounded-xl p-3">
                <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider mb-1">Company Size</p>
                <p className="text-white text-sm font-medium">{member.company_size || '—'}</p>
              </div>
              {member.business_name && (
                <div className="bg-ea-dark-card rounded-xl p-3 col-span-2">
                  <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider mb-1">Business Name</p>
                  <p className="text-white text-sm font-medium">{member.business_name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Demo qualifying data */}
          {(member.client_volume || member.enquiry_frequency) && (
            <div className="px-6 py-5 border-b border-ea-dark-border space-y-3">
              <div className="flex items-center gap-2">
                <p className="text-ea-gray/50 text-xs font-semibold uppercase tracking-wider">Demo Qualifying Data</p>
                <span className="px-2 py-0.5 bg-ea-blue/10 border border-ea-blue/20 rounded-full text-ea-blue text-[10px] font-semibold">From booking form</span>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {member.client_volume && (
                  <div className="bg-ea-dark-card rounded-xl p-3 flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 shrink-0">
                      <i className="ri-group-line text-ea-blue text-sm"></i>
                    </div>
                    <div>
                      <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider mb-0.5">Weekly Client / Job Volume</p>
                      <p className="text-white text-sm font-medium">{member.client_volume}</p>
                    </div>
                  </div>
                )}
                {member.enquiry_frequency && (
                  <div className="bg-ea-dark-card rounded-xl p-3 flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 shrink-0">
                      <i className="ri-chat-poll-line text-ea-blue text-sm"></i>
                    </div>
                    <div>
                      <p className="text-ea-gray/50 text-[10px] uppercase tracking-wider mb-0.5">Enquiry Frequency</p>
                      <p className="text-white text-sm font-medium">{member.enquiry_frequency}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Interests */}
          {interestList.length > 0 && (
            <div className="px-6 py-5 border-b border-ea-dark-border space-y-3">
              <p className="text-ea-gray/50 text-xs font-semibold uppercase tracking-wider">Automation Interests</p>
              <div className="flex flex-wrap gap-2">
                {interestList.map((interest) => (
                  <span
                    key={interest}
                    className="px-3 py-1.5 bg-ea-blue/10 border border-ea-blue/20 rounded-full text-ea-blue text-xs font-medium"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="px-6 py-5">
            <p className="text-ea-gray/50 text-xs font-semibold uppercase tracking-wider mb-3">Account Details</p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-ea-gray/60 text-xs">Joined</span>
                <span className="text-white text-xs font-medium">{joinDate}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-ea-gray/60 text-xs">MailerLite synced</span>
                <span className={`text-xs font-medium ${member.newsletter_opt_in ? 'text-green-400' : 'text-ea-gray/40'}`}>
                  {member.newsletter_opt_in ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-ea-dark-border shrink-0">
          <a
            href={`mailto:${member.email}`}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-ea-blue hover:bg-ea-blue-electric rounded-xl text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line text-sm"></i>
            Send Email
          </a>
        </div>
      </div>
    </>
  );
};

export default MemberDetailPanel;
